import { API } from './axios';
import { ProductCodes } from './types';

/**
 * Shared reference lists — product codes, places, course names.
 *
 * These used to hold a module-level promise cache each (`productCodesCache`,
 * `placesCache`, `coursesCache`) with a `force` flag to bust them. That worked,
 * but it meant three hand-rolled caches with three subtly different lifetimes,
 * none of which could be invalidated from anywhere except the module that owned
 * it, and none of which was keyed by company — a company switch kept serving
 * the previous network's places.
 *
 * They are now plain fetchers. Caching, deduplication, invalidation and
 * **cancellation** belong to TanStack Query (`lib/query-client.ts` holds the
 * keys); this file's job is the wire contract and nothing else.
 *
 * Every fetcher takes an optional `AbortSignal`. The old `cancelled` booleans
 * dropped a stale response but still paid for it; an aborted request is not
 * sent to completion at all.
 */

// `networkId` is `networks.id`, NOT the HQ company id (FRA-20261025-001) —
// see lib/reference-queries.ts.
export function getProductCodes(
    networkId: number,
    signal?: AbortSignal,
): Promise<ProductCodes[]> {
    return API.get<{ data: ProductCodes[] }>(`/network/${networkId}/product_codes`, { signal })
        .then(res => res.data.data);
}

// `type` is nullable by contract: NULL / absent IS the default type, and only
// non-default types carry a stored value (api-v1's Product_Code resource).
export function isExternalProductCode(code: Pick<ProductCodes, 'type'>): boolean {
    return code.type === 'external';
}

/**
 * The external subset, DERIVED from the shared list rather than fetched.
 *
 * `GET network/{id}/product_codes?type=external` exists and both external-row
 * surfaces used to call it — a second request for a strict subset of a list the
 * app already holds. Filtering a cached list costs nothing; keep it that way.
 */
export function selectExternalProductCodes(list: ProductCodes[]): ProductCodes[] {
    return list.filter(isExternalProductCode);
}

// ---- Places ----
//
// `GET /places` is NETWORK-WIDE for this app: api-v1's listing applies
// `Places::network(SCOPE_DOWNSTREAM)` whenever the caller sends
// `X-ZOOZA-SCOPE: downstream`, which our axios instance sets on every request
// (see lib/axios.ts). So one call names every place in the network — no
// per-company fan-out, and nothing period-scoped.
export interface PlaceRef {
    id: number;
    name: string;
}

export function getPlaces(signal?: AbortSignal): Promise<PlaceRef[]> {
    return API.get<{ data: Record<string, unknown>[] }>('/places', { signal }).then(res =>
        (res.data?.data ?? [])
            .map(row => ({
                id: Number(row.id),
                name: String(row.name ?? '').trim(),
            }))
            .filter(p => Number.isFinite(p.id) && p.name !== ''),
    );
}

// ---- Courses ----
//
// `GET network/{network_id}/courses` with NO `X-ZOOZA-NETWORK-COMPANY` header
// is the network-wide id→name lookup (api-v1, product-code-type handoff
// 2026-07-28): every company of the network in ONE response, carrying `id`,
// `name` and `company_id` and nothing else — so it costs a fraction of the full
// `/courses`. Name-ordered and unpaginated by design.
//
// Sending the header still returns that ONE company's full course objects,
// unchanged. That used to be the only option, which is why naming a network's
// courses meant one request per company; it is now a single call.
//
// Still gated on a report actually rendering the course dimension rather than
// loaded at app start: cheap is not free, and most pages never name a course.
//
// The parameter was always NAMED `networkId` but was handed the HQ company id
// until FRA-20261025-001 — the name is why it read as already correct. It now
// receives `networks.id`.
export interface CourseRef {
    id: number;
    name: string;
    company_id?: number;
}

export function getCourses(networkId: number, signal?: AbortSignal): Promise<CourseRef[]> {
    return API.get<Record<string, unknown>[] | { data: Record<string, unknown>[] }>(
        `/network/${networkId}/courses`,
        { signal },
    ).then(res => {
        // The lookup answers with a bare array; the per-company shape wraps in
        // `data`. Read both rather than depend on which one this deployment
        // sends.
        const payload = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        return payload
            .map(row => ({
                id: Number(row.id),
                name: String(row.name ?? '').trim(),
                company_id: row.company_id == null ? undefined : Number(row.company_id),
            }))
            .filter(c => Number.isFinite(c.id) && c.name !== '');
    });
}
